import { Link } from '@chakra-ui/next-js';
import { Flex, Heading, Text, useDisclosure } from '@chakra-ui/react';
import { useTranslation } from 'next-i18next';
import { FC, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { ActionButtons, Form, getConsentStatus, pushConsent, TConsentMode } from '../../shared';
import translation from './lib/translations';
import Settings from './Settings';

const formId = 'cookie_form';

const CookieWidget: FC<{ privacyHref?: string; cookieHref?: string }> = ({
  privacyHref,
  cookieHref,
}) => {
  const { i18n } = useTranslation();
  const t = translation(i18n.language);
  const [isOpen, setIsOpen] = useState(false);
  const { isOpen: isOpenSettings, onToggle: onToggleSettings } = useDisclosure();

  const defaultValues = useMemo(() => getConsentStatus(), []);

  const methods = useForm<TConsentMode>({ defaultValues: { ...defaultValues, necessary: true } });

  const onClose = () => setIsOpen(false);

  const saveCookies = (data?: TConsentMode) => {
    let prepData = data;

    if (!prepData) {
      prepData = Object.fromEntries(
        Object.keys(methods.getValues()).map((key) => [key, true]),
      ) as TConsentMode;
    }

    pushConsent('update', prepData);
    onClose();
  };

  const handleAcceptAll = () => {
    saveCookies();
  };

  const onSaveForm = () => {
    saveCookies(methods.getValues());
  };

  useEffect(() => {
    setIsOpen(!defaultValues?.necessary);
  }, []);

  if (!isOpen) {
    return null;
  }

  return (
    <Flex
      bg='bg.dark'
      borderRadius='lg'
      bottom={{ base: 'sm', md: 'xl' }}
      color='white'
      flexDir='column'
      gap='2xs'
      h='fit-content'
      left='50%'
      maxH='90dvh'
      mt='auto'
      overflowY='auto'
      p={{ base: 'md', md: 'xl' }}
      position='fixed'
      transform='translate(-50%)'
      w={{ base: '90%', md: 'container.sm', xl: 'container.lg' }}
      zIndex='sticky'
    >
      <Heading
        color='white'
        mb='xs'
      >
        {t.head}
      </Heading>
      <Text>{t.description}</Text>

      <Flex
        flexDir='row'
        gap='sm'
      >
        {privacyHref && (
          <Link
            _hover={{ textDecoration: 'underline' }}
            color='primary.pure'
            href={privacyHref}
            target='_blank'
          >
            {t.privacyLink}
          </Link>
        )}
        {cookieHref && (
          <Link
            _hover={{ textDecoration: 'underline' }}
            color='primary.pure'
            href={cookieHref}
            target='_blank'
          >
            {t.cookieLink}
          </Link>
        )}
      </Flex>
      <Form
        childrenContainerProps={{ mt: 'lg', gap: 'md' }}
        formId={formId}
        methods={methods}
        onSubmit={() => {}}
      >
        <Settings
          isOpen={isOpenSettings}
          onSave={onSaveForm}
        />
        <ActionButtons
          mt='0'
          negativeButtonProps={{ variant: 'outline', colorScheme: 'white' }}
          negativeButtonText={t.previewBtn}
          positiveButtonText={t.acceptBtn}
          showConfirmOnDelete={false}
          onCancel={onToggleSettings}
          onSave={handleAcceptAll}
        />
      </Form>
    </Flex>
  );
};

export default CookieWidget;
